import { Injectable } from '@angular/core';
import { COPYS } from '@app/constants';
import { AssessmentSelfManagementInformationProps } from '@app/interface/assessment-self-management-information-props.interface';
import { AssessmentSelfManagementInformationComponent } from '@app/modules/assessment-self-management/components/assessment-self-management-information/assessment-self-management-information.component';
import { HtmlSanitizationService } from '@app/services/html-sanitization.service';
import { ModalService } from '@app/services/modal.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AssessmentConfirmationAlertService {
  private readonly showConfirmationAlert = new BehaviorSubject<boolean>(false);
  public readonly showConfirmationAlert$ =
    this.showConfirmationAlert.asObservable();
  public resetFormCallback: () => void = () => undefined;

  constructor(
    private readonly modalService: ModalService,
    private readonly htmlSanitizationService: HtmlSanitizationService
  ) {}

  updateShowConfirmationAlert(value: boolean): void {
    this.showConfirmationAlert.next(value);
  }

  closeModal(): void {
    this.modalService.close();
  }

  createModal(createModalProps: {
    title: string;
    description: string;
    callback: () => void;
    confirmText: string;
    showActions: boolean;
  }): void {
    const {
      callback,
      description,
      title,
      confirmText,
      showActions,
    } = createModalProps;
    const props: Partial<AssessmentSelfManagementInformationProps> = {
      descriptionHtml: this.htmlSanitizationService.sanitize(description),
      showIcon: false,
      showActions,
      cancelText: COPYS.GoBack,
      confirmText,
    };
    const componentRef = this.modalService.loadComponent(
      AssessmentSelfManagementInformationComponent,
      props
    );
    this.modalService.updateHeader(title, true);
    this.modalService.open();
    if (!componentRef) return;
    const { confirmEvent, cancelEvent } = componentRef.instance;
    confirmEvent.subscribe(() => {
      this.updateShowConfirmationAlert(false);
      callback();
      this.resetFormCallback();
      this.modalService.close();
    });
    cancelEvent.subscribe(() => this.modalService.close());
  }

  loadPendingFeedbackModal(callback: () => void, showActions = true): void {
    this.createModal({
      title: COPYS.PendingFeedbackModalTitle,
      description: COPYS.PendingFeedbackModalDescription,
      callback,
      confirmText: COPYS.DiscardSupportsAndContinue,
      showActions,
    });
  }

  loadPendingFeedbackModalWithOutActions(): void {
    this.loadPendingFeedbackModal(() => undefined, false);
  }

  loadFinalizeAssessmentModal(callback: () => void): void {
    this.createModal({
      title: COPYS.FinalizeAssessmentModalTitle,
      description: COPYS.FinalizeAssessmentModalDescription,
      callback,
      confirmText: COPYS.FinishSelfAssessment,
      showActions: true,
    });
  }

  loadFinalizeAssessmentAndDiscardSupportsModal(callback: () => void): void {
    this.createModal({
      title: COPYS.FinalizeAssessmentModalTitle,
      description: COPYS.FinalizeAssessmentAndDiscardSupportsModalDescription,
      callback,
      confirmText: COPYS.FinishAndDiscardSupports,
      showActions: true,
    });
  }
}
